import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, ModalOpen, StateManager, useBCMSContext, useBCMSModal, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import FormContainer, { FormButtonContainer, FormRow } from 'components/shared/containers/FormContainer';
import { UICancelButton, UIPrimaryButton } from 'components/ui/UIButton';
import UIDatePicker from 'components/ui/UIDatePicker ';
import UIRadio from 'components/ui/UIRadio';
import UIExplanationTextInput from 'components/ui/UITextInput/components/UIExplanationTextInput';
import Classes, { Transaction, TransactionDispute } from 'model';
import React, { useEffect } from 'react';
import DisputeProgressWidget from '../widgets/DisputeProgressWidget';

/**
 * @function DisputeContactResolveView
 * @return {React.Component}
 * @exported true
 * @description The view that displays the questions about contacting the merchant.
 */

export default function DisputeContactResolveView(props) {
  const bcmsContext = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, bcmsContext);
  const navigate = useRoute();
  const params = useParams();
  const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};
  const paramsTransactionID = useParams().transactionID ?? "0";
  const data = { ...getDefaultData(paramsTransactionID), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    update: type => (val, label) => {
      let answer = label.toLowerCase();
      const crud = BCMSStateManagerCRUD.init(state);

      if(type === "didYouAttemptToContactTheMerchantToResolveTheIssue") {
        if(answer === "yes") {
          crud
          .update([ "data", "dispute", "isThereAnyAdditionalInformationYouWouldLikeToProvide" ], "")
          .update([ "data", "dispute", "additionalInfoExplanation" ], "");
        } else {
          crud
          .update([ "data", "dispute", "whenDidYouAttemptToResolveThisissue" ], "")
          .update([ "data", "dispute", "whatWasTheMerchantsResponseAndMethodOfCommunication" ], "")
          .update([ "data", "dispute", "isThereAnyAdditionalInformationYouWouldLikeToProvide" ], "")
          .update([ "data", "dispute", "isThereAnyAdditionalInformationYouWouldLikeToProvide" ], "")
          .update([ "data", "dispute", "additionalInfoExplanation" ], "");
        }
      }

      if(type === "isThereAnyAdditionalInformationYouWouldLikeToProvide") {
        if(answer === "no") {
          crud.update([ "data", "dispute", "additionalInfoExplanation" ], "");
        }
      }
      
      crud
        .update([ "data", "dispute", type ], answer)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    updateCustomSelect: type => val => {
      let reason = val.toLowerCase();
      
      BCMSStateManagerCRUD.init(state)
        .update([ "data", "dispute", type ], reason)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    // User might select a date from a calendar or type a date.
    updateCustomCalendar: type => val => {
      const crud = BCMSStateManagerCRUD.init(state);

      // Check if the input value is empty if the user is typing a date.
      if(val === "") {
        //crud.update([ "data", "didSelectWhenDidYouAttemptToResolveThisissue"], false)
        crud.update([ "data", "dispute", "whenDidYouAttemptToResolveThisissue"], "")
        
        crud.submit(setState);
        return;
      }

      if(val instanceof BCMSDate) {
        crud.update([ "data", "dispute", type ], val)
      } else {
        let validatedTypeDate = new Date(val);
        let typedDateIsValid = validatedTypeDate.toString() !== "Invalid Date";

        if(typedDateIsValid) {
          validatedTypeDate = BCMSDate.init(validatedTypeDate);
        } else {
          validatedTypeDate = BCMSDate.init(0);
        }

        crud.update([ "data", "dispute", type ], validatedTypeDate)
      }

      crud.update([ "data", "didSelectWhenDidYouAttemptToResolveThisissue"], true)
      crud.submit(setState);
    },

    updateTextInput: type => (arg1, arg2, arg3) => {
      BCMSStateManagerCRUD.init(state)
        .update([ "data", "dispute", type ], arg1)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    cancel: () => {
      navigate(`/home/transactiondetails/${ params.cardID }/${ params.transactionID }`);
    },

    confirm: () => {
      data.dispute.id = TransactionDispute.nextID();

      BCMSRecord.batch([
        TransactionDispute.create({ ...data.dispute.record }),
        Transaction.update({ ...data.dispute.transaction.record, transactionDispute: data.dispute })
      ], bcmsContext, Classes);
      
      goStep(6, { data, ui });
    },
  });

  return(
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = transactionID => {
  const transaction = Transaction.initByID(transactionID);
  const dispute = BCMSDefaultModel.transactionDispute();
  dispute.transaction = transaction;

  return {
    //didSelectWhenDidYouAttemptToResolveThisissue: false,
    dispute
  };
}

export const getDefaultUI = () => ({
  showConfirmModal: false,
});

function PageContent(props) {
  const { data, ui, next, cancel, confirm, update, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};

  const nextButtonEnabledCondition1 = dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "no"
  && dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "no";

  const nextButtonEnabledCondition2 = dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "yes"
  && dispute.whenDidYouAttemptToResolveThisissue !== ""
  //&& data.didSelectWhenDidYouAttemptToResolveThisissue
  && dispute.whatWasTheMerchantsResponseAndMethodOfCommunication !== ""
  && dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "no";

  const nextButtonEnabledCondition3 = dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "yes"
  && dispute.whenDidYouAttemptToResolveThisissue !== ""
  //&& data.didSelectWhenDidYouAttemptToResolveThisissue
  && dispute.whatWasTheMerchantsResponseAndMethodOfCommunication !== ""
  && dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "yes"
  && dispute.additionalInfoExplanation !== "";

  const nextButtonEnabledCondition4 = dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "no"
  && dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "yes"
  && dispute.additionalInfoExplanation !== "";
  

  const nextButtonIsEnabled = nextButtonEnabledCondition1 || nextButtonEnabledCondition2 || nextButtonEnabledCondition3 || nextButtonEnabledCondition4;
  const onCancel = () => cancel();
  const onConfirmClick = () => confirm();

  const yesOptionDataPoint = { dataPoint: "Attempted_Merchant_Contact_Yes_Option" };
  const noOptionDataPoint = { dataPoint: "Attempted_Merchant_Contact_No_Option" };
  const submitButtonDataPoint = { dataPoint: "Submit_Button" };

  // This "useEffect" code is used to open the confirmation modal in capture components.
  const { close, open } = useBCMSModal();
  useEffect(() => {
    if(ui.showConfirmModal === true) {
      open("SubmitDisputeTransactionModal");
    } else {
      close("SubmitDisputeTransactionModal");
    }
  }, [ ui.showConfirmModal ]);

  return (
    <div className="dispute-contact-resolve-view">
        <DisputeProgressWidget step="3" />
        <FormContainer>
          <FormRow>
            <p className="form-label">Did you attempt to contact the merchant to resolve the issue? *</p>
            <UIRadio label="Yes" { ...yesOptionDataPoint } value={ dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "yes" } 
            onChange={ update("didYouAttemptToContactTheMerchantToResolveTheIssue") } />
            <UIRadio label="No" { ...noOptionDataPoint } value={ dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "no" }
            onChange={ update("didYouAttemptToContactTheMerchantToResolveTheIssue") } />
          </FormRow>

          { dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "yes"
          && <ContactMerchantWidget /> }
          { dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "yes"
          && <AdditionalInformationWidget /> }
          { dispute.didYouAttemptToContactTheMerchantToResolveTheIssue === "no"
          && <AdditionalInformationWidget /> }
          {/*<ContactMerchantWidget />
          <AdditionalInformationWidget />*/}
          
          <FormButtonContainer>
          <ModalOpen id="SubmitDisputeTransactionModal" className={ nextButtonIsEnabled ? "" : "disabled" }
          data={ { onConfirmClick: onConfirmClick } }>
            <UIPrimaryButton text="Submit" { ...submitButtonDataPoint }/>
          </ModalOpen>
            {/*<ModalOpen id="SubmitDisputeTransactionModal">
              <UIPrimaryButton text="Submit" />
        </ModalOpen>*/}
            <ModalOpen id="CancelDisputeTransactionModal" data={ { onCancel: onCancel } }>
              <UICancelButton text="Cancel" />
            </ModalOpen>
          </FormButtonContainer>
        </FormContainer>
    </div>
  );
}

/**
 * @function ContactMerchantWidget
 * @return {React.Component}
 * @exported false
 * @description The widget that asks questions about contacting the merchant.
 */

function ContactMerchantWidget(props) {
  const { data, next, cancel, confirm, update, updateCustomCalendar, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};
  const datePickerDataPoint = { "data-point": "Attempted_Resolve_Date" };
  const merchantResponseExplanationDataPoint = { dataPoint: "Merchant_Response_Explanation_Text_Input" };

  return(
    <div className="contact-merchant-widget">
        <FormRow>
          <p className="form-label">When did you attempt to resolve this issue? *</p>
          <UIDatePicker dateLabel="Date (MMM DD, YYYY)" { ...datePickerDataPoint } allowPast={ true } allowFuture={ false } value={ dispute.whenDidYouAttemptToResolveThisissue } onChange={ updateCustomCalendar("whenDidYouAttemptToResolveThisissue") } />
        </FormRow>
        <FormRow>
          <p className="form-label">What was the merchant's response and method of communication?</p>
         
          <UIExplanationTextInput maxLength="300" { ...merchantResponseExplanationDataPoint } placeholderText="Phone call, email, etc." className="has-placeholder" value={ dispute.whatWasTheMerchantsResponseAndMethodOfCommunication } 
          onChange={ updateTextInput("whatWasTheMerchantsResponseAndMethodOfCommunication") } />
        </FormRow>
    </div>
  );
}

/**
 * @function AdditionalInformationWidget
 * @return {React.Component}
 * @exported false
 * @description The widget that shows if a user wants to add additional information
 */

function AdditionalInformationWidget(props) {
  const { data, next, cancel, update, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};
  const yesOptionDataPoint = { dataPoint: "Additional_Info_Yes_Option" };
  const noOptionDataPoint = { dataPoint: "Additional_Info_No_Option" };


  return(
    <div className="additional-information-widget">
      <FormRow>
        <p className="form-label">Is there any additional information you would like to provide? *</p>
        <UIRadio label="Yes" { ...yesOptionDataPoint } value={ dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "yes" }
        onChange={ update("isThereAnyAdditionalInformationYouWouldLikeToProvide") } />
        <UIRadio label="No" { ...noOptionDataPoint } value={ dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "no" }
        onChange={ update("isThereAnyAdditionalInformationYouWouldLikeToProvide") } />
      </FormRow>
      { dispute.isThereAnyAdditionalInformationYouWouldLikeToProvide === "yes" && 
      <FormRow>
        <UIExplanationTextInput maxLength="300" placeholder="Explanation *" value={ dispute.additionalInfoExplanation } 
          onChange={ updateTextInput("additionalInfoExplanation") } />
      </FormRow>
      }
    </div>
  );
}